import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  Mail,
  Play,
  Trash2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface ScheduledReportsProps {
  templateId?: string;
  templateName?: string;
  onClose?: () => void;
}

export default function ScheduledReports({ templateId, templateName, onClose }: ScheduledReportsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [scheduleType, setScheduleType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY');
  const [scheduleDay, setScheduleDay] = useState<number>(1);
  const [scheduleTime, setScheduleTime] = useState('08:00');
  const [emails, setEmails] = useState('');

  // Fetch scheduled reports
  const { data: scheduledReports, isLoading } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: async () => {
      const response = await api.get('/reports/scheduled');
      return response.data.data || [];
    },
  });

  // Schedule report mutation
  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!templateId) throw new Error('No template selected');
      
      const emailList = emails.split(',').map(e => e.trim()).filter(Boolean);
      if (emailList.length === 0) throw new Error('At least one email is required');
      
      const response = await api.post(`/reports/templates/${templateId}/schedule`, {
        type: scheduleType,
        day: scheduleDay,
        time: scheduleTime,
        emails: emailList,
      });
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Report scheduled successfully' });
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      onClose?.();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to schedule report', 
        variant: 'destructive' 
      });
    },
  });

  // Unschedule report mutation
  const unscheduleMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/reports/templates/${id}/schedule`);
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Report unscheduled' });
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to unschedule report', variant: 'destructive' });
    },
  });

  // Run report now mutation
  const runNowMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/reports/templates/${id}/run`);
      return response.data;
    },
    onSuccess: (data) => {
      toast({ 
        title: data.success ? 'Success' : 'Error', 
        description: data.message,
        variant: data.success ? 'default' : 'destructive',
      });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to run report', variant: 'destructive' });
    },
  });

  const formatNextRun = (date: string) => {
    if (!date) return 'Not scheduled';
    const d = new Date(date);
    return d.toLocaleString();
  };

  // If we're scheduling a specific template
  if (templateId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule Report
          </CardTitle>
          <CardDescription>
            Set up automatic email delivery for: <strong>{templateName}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <div className="flex gap-2">
                {(['DAILY', 'WEEKLY', 'MONTHLY'] as const).map((type) => (
                  <Button
                    key={type}
                    variant={scheduleType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setScheduleType(type)}
                  >
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>
            </div>

            {scheduleType === 'WEEKLY' && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={scheduleDay}
                  onChange={(e) => setScheduleDay(parseInt(e.target.value))}
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}

            {scheduleType === 'MONTHLY' && (
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={scheduleDay}
                  onChange={(e) => setScheduleDay(parseInt(e.target.value))}
                >
                  {Array.from({ length: 28 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Time
            </Label>
            <Input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Recipients
            </Label>
            <Input
              placeholder="email1@example.com, email2@example.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Separate multiple emails with commas
            </p>
          </div>

          <div className="flex gap-2 justify-end">
            {onClose && (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button 
              onClick={() => scheduleMutation.mutate()}
              disabled={scheduleMutation.isPending || !emails.trim()}
            >
              {scheduleMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4 mr-2" />
              )}
              Schedule Report
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // List of scheduled reports
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Scheduled Reports</h3>
          <p className="text-sm text-muted-foreground">
            Reports that are automatically sent via email
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : scheduledReports && scheduledReports.length > 0 ? (
        <div className="space-y-3">
          {scheduledReports.map((report: any) => (
            <Card key={report.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    'bg-blue-100 text-blue-600'
                  )}>
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">{report.name}</h4>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {report.scheduleType} at {report.scheduleTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {report.scheduleEmails?.split(',').length || 0} recipients
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Next run: {formatNextRun(report.nextRunAt)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runNowMutation.mutate(report.id)}
                    disabled={runNowMutation.isPending}
                  >
                    {runNowMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unscheduleMutation.mutate(report.id)}
                    disabled={unscheduleMutation.isPending}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <AlertCircle className="w-8 h-8 mb-2 opacity-50" />
            <p>No scheduled reports</p>
            <p className="text-xs">Save a report template and schedule it for automatic delivery</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
